// biome-ignore lint/style/useNodejsImportProtocol: we use the events module, but biome can't detect it in a monorepo yet
import EventEmitter from 'events';

import {
    type ContentOperation,
    ContentOperationStatus,
    type ContentOperationUpdate,
} from '@gitbook/hive-core';

import type { HiveClient } from './HiveClient';

/**
 * Controller for a long background operation on a content.
 */
export class HiveContentOperation<Payload extends Record<string, string> = Record<string, string>> {
    public readonly client: HiveClient;

    public readonly contentId: string;
    public readonly path: string;
    public readonly pathParams: Record<string, string>;

    public id: string | undefined = undefined;
    public state: ContentOperation<Payload> | null = null;
    private events = new EventEmitter();

    constructor(
        client: HiveClient,
        contentId: string,
        path: string,
        pathParams: Record<string, string>,
        existingOperationId?: string
    ) {
        this.client = client;
        this.contentId = contentId;
        this.path = path;
        this.pathParams = pathParams;

        if (existingOperationId) {
            this.id = existingOperationId;
        }
    }

    /**
     * Create the operation in the server and lock the content.
     */
    public async start(
        initialState: Partial<ContentOperation<Payload>> = {}
    ): Promise<ContentOperation<Payload>> {
        if (this.id) {
            throw new Error('Operation already started');
        }

        const response = await this.client.request(
            this.contentId,
            this.path,
            {
                path: this.pathParams,
            },
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(initialState),
            }
        );
        const state = (await response.json()) as ContentOperation<Payload>;
        this.setState(state);

        return state;
    }

    /**
     * Update the progress of the operation.
     */
    public async update(update: ContentOperationUpdate): Promise<ContentOperation<Payload>> {
        if (!this.id) {
            throw new Error(
                'Operation not started or HiveContentOperation() is called without an existing operation ID'
            );
        }

        const response = await this.client.request(
            this.contentId,
            this.path,
            {
                path: this.pathParams,
            },
            {
                method: 'PUT',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...update,
                    id: this.id,
                }),
            }
        );
        const state = (await response.json()) as ContentOperation<Payload>;
        this.setState(state);

        return state;
    }

    /**
     * Mark the operation as completed successfully.
     */
    public async markAsSucceeded(): Promise<ContentOperation<Payload>> {
        return this.update({
            status: ContentOperationStatus.SUCCESS,
        });
    }

    /**
     * Update the local state.
     */
    public setState(state: ContentOperation<Payload>): void {
        this.id = state.id;
        this.state = state;
        this.events.emit('update', state);
    }

    /**
     * Listen to updates to the operation.
     */
    public listen(callback: () => void): () => void {
        this.events.on('update', callback);

        return () => {
            this.events.off('update', callback);
        };
    }
}
